// 介錯の致命「執行」を弾かれた時、刃で床をこすりながら後ろへ下がる (10-21 作者が答えるページで B を選んだ・台帳 docs/ledger/executioner.md)。
//   作者メモ「武器で床をこすりながら下がるのに合わせてエフェクトを付けてほしい。砂埃が良さそう」。
//   動き = 主人公の押し戻し (src/exec_parry.ts execPush) と同じ時計・同じ進み方で、主人公と逆向きへ SLIDE_PX だけ滑る。
//     boss.x を本当に動かす (描画だけずらすと、待機へ戻った時に元の位置へ跳んで見える)。壁で止まる。
//   砂埃 = 体の後ろ下の刃先 (TIP_BEHIND_PX) が通った所に、床の擦り跡と低い土煙 (騎士の踏み込みと同じ knight_lunge_dust)。
//   砲を持っている時は弾かれの絵が無い (反らすだけ) ので滑らない。前の版 (DEV ?execParriedArt=old) も滑らない。
//   10-22 剣舞の役者も同じ動きで下がる (ユーザー「剣舞も後ずさりさせてください。砂ぼこりも出して」・前の版 = DEV ?bladeSlide=off)。
//     絵 = 2本の刀を前の地面に突き立てて踏ん張る姿 (止まった1枚) なので、歩かず踏ん張ったまま押し滑る。
//     足が氷の上のように滑って見えないよう、出だしが一番速く止まり際は遅い (主人公の押し戻しと同じ進み方)・
//     前に突き立てた2本の刀の先が床に溝を引き、前足は擦り跡・押される後ろ足は土煙を上げる。
import { arenaBoundsFor } from './balance'
import { EXEC_PUSH, EXEC_PUSH_DUST, execPushProgress, execPushSliding } from './exec_parry'
import { execParriedArtNew } from './exec_parry_recoil'
import type { Boss, FxEvent, Player } from './types'

export const EXEC_PARRIED_SLIDE = {
  /** 下がる距離 (px)。主人公の押し (介錯 150px) より短い = 重い方が少しだけ下がる。 */
  slidePx: 56,
  /** 刃先の位置 (ボス中心から後ろへ・ゲーム px・弾かれの絵 exec_parried_1021 の刃先 x 1409 と置く点 606.3 の差 × 1画素の大きさ 0.1502 = 実測 120.6)。 */
  tipBehindPx: 120,
  /** 擦り跡・土煙を出す間隔。 */
  everyPx: 10,
} as const

/** 剣舞の役者の弾かれの絵 (exec_parried_1021・1464x955・置く点 x 775・1画素 = 186 / 1437 × 1.1 = 0.1424 ゲーム px)。
 *  足と刀の先は絵の一番下の不透明な所 (scripts なしで列ごとに測った・床は y 934〜950)。 前 = 主人公の側。 */
export const BLADE_PARRIED_SLIDE = {
  /** 下がる距離 (px)。 構えの幅 (前足〜後ろ足 約 143px) の 4 割ほど = 踏ん張った足で止まって見える長さ。 介錯と同じ。 */
  slidePx: 56,
  /** 2本の刀の先 (ボス中心から前へ・絵の x 40 と 145 → (775 − x) × 0.1424)。 */
  tipsAheadPx: [105, 90],
  /** 前足のつま先 (前へ・絵の x 390)。 */
  frontFootAheadPx: 55,
  /** 後ろ足のかかと (後ろへ・絵の x 1390)。 */
  backFootBehindPx: 88,
} as const
let bladeSlide = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return true
  return new URLSearchParams(window.location.search).get('bladeSlide') !== 'off'
})()
export function bladeSlideOn(): boolean { return bladeSlide }
export function setBladeSlideForTest(on: boolean): void { bladeSlide = on }

function slidePxOf(bossId: string): number | undefined {
  if (bossId === 'the-executioner') return EXEC_PARRIED_SLIDE.slidePx
  if (bossId === 'blade-dancer' && bladeSlide) return BLADE_PARRIED_SLIDE.slidePx
  return undefined
}

export type ExecParriedSlide = {
  /** 弾いた時のパリィの押した時刻 (主人公の押し戻しと組にする)。 */
  pressMs: number
  /** 下がる向き (+1 = 右へ)。 */
  dir: 1 | -1
  px: number
  traveledPx: number
}

/** このコマで下がり始める・下がり続ける (game.ts から毎コマ・boss = このコマの更新後・player = このコマの主人公)。 */
export function stepExecParriedSlide(boss: Boss, player: Player): Boss {
  const atk = boss.currentAttack
  const slidePx = slidePxOf(boss.id)
  if (slidePx === undefined || !atk?.execParried || !execParriedArtNew()) return boss
  const push = player.execPush
  let slide = atk.execParriedSlide
  if (!slide) {
    if (!push || boss.exec?.held === 'cannon') return boss
    const dir = (push.dir > 0 ? -1 : 1) as 1 | -1
    const b = arenaBoundsFor(boss.id)
    const room = Math.max(0, dir > 0 ? b.rightWall - boss.x : boss.x - b.leftWall)
    slide = { pressMs: push.pressMs, dir, px: Math.min(slidePx, room), traveledPx: 0 }
  }
  if (!push || push.pressMs !== slide.pressMs) return boss
  const target = slide.px * execPushProgress(push.elapsedMs / push.durationMs, push.durationMs, EXEC_PUSH[push.version])
  const dx = slide.dir * (target - slide.traveledPx)
  return { ...boss, x: boss.x + dx, currentAttack: { ...atk, execParriedSlide: { ...slide, traveledPx: target } } }
}

/** 刃先が床をこすった所の擦り跡と土煙 (描くだけ)。prev / next = このコマの前後のボス。 */
export function execParriedSlideDustFx(prev: Boss, next: Boss, player: Player, now: number): FxEvent[] {
  const a = prev.currentAttack?.execParriedSlide, b = next.currentAttack?.execParriedSlide
  // 足元が火の粉だけの版 (DEV ?execPush=sekiro) では土煙を出さない (主人公の足元と揃える)。
  if (!b || b.px <= 0 || !player.execPush || EXEC_PUSH[player.execPush.version].feet !== 'dust') return []
  const before = a && a.pressMs === b.pressMs ? a.traveledPx : 0
  const S = EXEC_PARRIED_SLIDE
  if (Math.floor(b.traveledPx / S.everyPx) <= Math.floor(before / S.everyPx) && !(before === 0 && b.traveledPx > 0)) return []
  const sliding = execPushSliding(player.execPush)
  // 速さで量を変える (出だしが一番濃い・止まり際は薄い)。
  const amount = EXEC_PUSH_DUST.minAmount + (EXEC_PUSH_DUST.maxAmount - EXEC_PUSH_DUST.minAmount) * (1 - b.traveledPx / b.px)
  if (next.id === 'blade-dancer') {
    const B = BLADE_PARRIED_SLIDE
    const at = (boss: Boss, off: number) => boss.x + b.dir * off
    const out: FxEvent[] = B.tipsAheadPx.map((ahead) => ({ kind: 'knight_lunge_dust' as const, variant: 'scuff' as const,
      x: at(prev, -ahead), x2: at(next, -ahead), y: next.y, t: now, dir: b.dir, amount }))
    out.push({ kind: 'knight_lunge_dust', variant: 'scuff', x: at(prev, -B.frontFootAheadPx), x2: at(next, -B.frontFootAheadPx), y: next.y, t: now, dir: b.dir, amount: amount * 0.6 })
    if (sliding) {
      // 押される後ろ足の土煙は下がる向きへ・刀の先は掘った土を主人公の側へ跳ね上げる。
      out.push({ kind: 'knight_lunge_dust', variant: 'kick', x: at(next, B.backFootBehindPx), y: next.y, t: now, dir: b.dir, amount: amount * 0.8 })
      out.push({ kind: 'knight_lunge_dust', variant: 'kick', x: at(next, -B.tipsAheadPx[0]), y: next.y, t: now, dir: -b.dir, amount: amount * 0.5 })
    }
    return out
  }
  const x1 = next.x + b.dir * S.tipBehindPx
  const x0 = prev.x + b.dir * S.tipBehindPx
  const out: FxEvent[] = [{ kind: 'knight_lunge_dust', variant: 'scuff', x: x0, x2: x1, y: next.y, t: now, dir: b.dir, amount }]
  if (sliding) out.push({ kind: 'knight_lunge_dust', variant: 'kick', x: x1, y: next.y, t: now, dir: -b.dir, amount: amount * 0.8 })
  return out
}
